import Extent from "@arcgis/core/geometry/Extent";
import { SimpleFillSymbol } from "@arcgis/core/symbols";
import SimpleRenderer from "@arcgis/core/renderers/SimpleRenderer";
import Color from "@arcgis/core/Color";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer";
import LabelClass from "@arcgis/core/layers/support/LabelClass";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";

export enum PrintWithBasemap {
  UsaTOPO,
  Hybrid,
}

let BaseLayerTemplate = {
  id: "BASE_LAYERS",
  title: "Base Layers",
  opacity: 1,
  minScale: 0,
  maxScale: 0,
  token: "",
  url: "",
};

let UsaTopoBasemapTemplate = {
  id: "USA_Topo_Maps",
  opacity: 1,
  minScale: 0,
  maxScale: 0,
  url: "https://services.arcgisonline.com/ArcGIS/rest/services/USA_Topo_Maps/MapServer",
};

let WorldImageryBasemapTemplate = {
  id: "World_Imagery",
  opacity: 1,
  minScale: 0,
  maxScale: 0,
  url: "https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer",
  visibleLayers: [0],
};

let MapServerLayerTemplate = {
  id: "Map_Server_Labels",
  opacity: 0.9,
  minScale: 0,
  maxScale: 0,
  token: "",
  url: "",
  layerDefinition: {
    definitionExpression: "",
    drawingInfo: {
      renderer: new UniqueValueRenderer({
        field: "bGISAccepted",
        fieldDelimiter: ",",
        uniqueValueInfos: [
          {
            label: "Approved",
            value: "Approved",
            symbol: new SimpleFillSymbol({
              color: new Color("#00A6A6"),
              style: "solid",
              outline: {
                color: new Color("#51f7fd"),
                width: 1.75,
              },
            }),
          },
          {
            label: "Proposed",
            value: "Proposed",
            symbol: new SimpleFillSymbol({
              color: new Color("#F49F0A"),
              style: "solid",
              outline: {
                color: new Color("#ffe062"),
                width: 1.75,
              },
            }),
          },
        ],
      }).toJSON(),
    },
  },
};

type WebMapAsJson = {
  mapOptions: {
    extent: Extent;
    scale: number;
  } & any;
  operationalLayers: any[];
  layoutOptions: {
    titleText: string;
    customTextElements: { Date: string }[];
    legendOptions: {
      operationalLayers: any[];
    };
  } & any;
};
let WebMapAsJsonTemplate: WebMapAsJson = {
  mapOptions: {
    showAttribution: true,
    extent: null,
  },
  operationalLayers: [],
  layoutOptions: {
    titleText: "",
    authorText: "",
    copyrightText: "",
    customTextElements: [
      {
        Date: "3/26/2024, 12:06:43 PM",
      },
    ],
    scaleBarOptions: {
      metricUnit: "esriKilometers",
      metricLabel: "km",
      nonMetricUnit: "esriMiles",
      nonMetricLabel: "mi",
    },
    legendOptions: {
      operationalLayers: [],
    },
  },
};

export function buildWebMapAsJson(
  token: string,
  mapServerUrl: string,
  baseLayerUrl: string,
  extent: Extent,
  definitionExpression: string,
  printWithBasemap: PrintWithBasemap,
  title: string,
  scale: number | null = null,
): WebMapAsJson {
  let template: WebMapAsJson = structuredClone(WebMapAsJsonTemplate);

  template.mapOptions.extent = extent;
  if (scale) template.mapOptions.scale = scale;

  let baseLayersTemplate = structuredClone(BaseLayerTemplate);
  baseLayersTemplate.token = token;
  baseLayersTemplate.url = baseLayerUrl;

  let basemap;
  switch (printWithBasemap) {
    case PrintWithBasemap.UsaTOPO:
      basemap = UsaTopoBasemapTemplate;
      break;
    case PrintWithBasemap.Hybrid:
      basemap = WorldImageryBasemapTemplate;
      break;
  }
  template.layoutOptions.titleText = title;

  let mapServerLayerTemplate = structuredClone(MapServerLayerTemplate);
  mapServerLayerTemplate.id = title;
  mapServerLayerTemplate.token = token;
  mapServerLayerTemplate.url = mapServerUrl;
  mapServerLayerTemplate.layerDefinition.definitionExpression =
    definitionExpression;

  template.operationalLayers.push(
    basemap,
    baseLayersTemplate,
    mapServerLayerTemplate,
  );

  template.layoutOptions.customTextElements = [
    { Date: new Date().toLocaleString() },
  ];
  template.layoutOptions.legendOptions.operationalLayers = [
    mapServerLayerTemplate,
  ];

  return template;
}
